import { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft } from "lucide-react";
import { submitSchoolInquiry, errText } from "@/lib/api";
import { Field, TextInput, TextArea, Select, PillGroup, AckCheck, SectionHead, SubmitBar, SuccessPanel, FormShell } from "@/lib/forms";
import { PageHeader } from "@/components/Section";
import { CREATORS } from "@/data/characters";

const DISCIPLINES = CREATORS.map((c) => c.lane);
const INTERESTS = ["Portfolio projects", "Real briefs", "Experiential learning", "Lab / practicum", "Academic credit", "Cross-disciplinary work", "Mentorship", "Career readiness", "Paid opportunities when available", "Workshops"];
const FORMATS = ["Campaign Labs", "School-specific projects", "Cross-school collaboration", "Sponsored campaigns", "Portfolio reviews", "Workshops", "Panels", "Job fairs", "Showcases"];
const REQUIREMENTS = ["Affiliation agreement", "Evaluation forms", "Proof of hours", "Insurance requirements", "Background checks", "Site approval", "Supervision requirements"];

export default function ApplySchool() {
  const [v, setV] = useState({ institution: {}, contact: {}, programs: {}, partnership: {}, credit: {}, logistics: {} });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);

  const set = (k, val) => setV((p) => ({ ...p, [k]: val }));
  const setIn = (sec, k) => (val) => setV((p) => ({ ...p, [sec]: { ...p[sec], [k]: val } }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    if (!v.acknowledgment) return toast.error("The credit acknowledgment is required before submitting.");
    setLoading(true);
    try {
      const res = await submitSchoolInquiry(v);
      setDone(res);
      window.scrollTo({ top: 0 });
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        kicker="School Partner"
        titleLines={["Let's build for", "your students."]}
        subtitle="Tell us about your institution, your students, and what a partnership should accomplish. Fields with a gold star are required."
        dark
      />
      <section className="grain-dark bg-onyx py-14 sm:py-20">
        <div className="mx-auto max-w-[880px] px-4 sm:px-6">
          <Link to="/apply" className="mb-6 inline-flex items-center gap-2 font-head text-sm font-bold uppercase tracking-wide text-goldlight transition-colors hover:text-gold" data-testid="back-to-apply">
            <ArrowLeft size={16} /> All paths
          </Link>

          {done ? (
            <SuccessPanel
              title="Transmission received. Case file created."
              id={done.id}
              note="The Architect will review your request, and our team will reach out to talk through what a partnership could look like for your students. Academic, lab, practicum, or internship credit is always determined by your institution."
              emailNotice
            />
          ) : (
            <FormShell marker="for the students ✦">
              <form onSubmit={submit} className="space-y-5" data-testid="school-inquiry-form">
                <SectionHead n="01" title="Institution" />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Institution name" required><TextInput required value={v.institution_name} onChange={(x) => set("institution_name", x)} testId="field-institution" /></Field>
                  <Field label="Campus / location"><TextInput value={v.institution.campus_location} onChange={setIn("institution", "campus_location")} testId="field-campus" /></Field>
                  <Field label="Website"><TextInput value={v.institution.website} onChange={setIn("institution", "website")} testId="field-website" /></Field>
                </div>

                <SectionHead n="02" title="Contact" />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Contact name" required><TextInput required value={v.contact_name} onChange={(x) => set("contact_name", x)} testId="field-contact-name" /></Field>
                  <Field label="Email" required><TextInput required type="email" value={v.contact_email} onChange={(x) => set("contact_email", x)} testId="field-contact-email" /></Field>
                  <Field label="Title"><TextInput value={v.contact.title} onChange={setIn("contact", "title")} testId="field-title" /></Field>
                  <Field label="Department"><TextInput value={v.contact.department} onChange={setIn("contact", "department")} testId="field-department" /></Field>
                  <Field label="Phone"><TextInput value={v.contact.phone} onChange={setIn("contact", "phone")} testId="field-phone" /></Field>
                  <Field label="Preferred contact method"><Select value={v.contact.preferred_contact} onChange={setIn("contact", "preferred_contact")} options={["Email", "Phone", "Either"]} testId="field-preferred-contact" /></Field>
                </div>

                <SectionHead n="03" title="Programs & Students" />
                <Field label="Disciplines / programs"><PillGroup multi value={v.programs.disciplines} onChange={setIn("programs", "disciplines")} options={DISCIPLINES} testId="field-disciplines" /></Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Approximate student count"><TextInput value={v.programs.student_count} onChange={setIn("programs", "student_count")} testId="field-student-count" /></Field>
                  <Field label="Format"><Select value={v.programs.format} onChange={setIn("programs", "format")} options={["In-person", "Online", "Hybrid"]} testId="field-format" /></Field>
                  <Field label="Typical student experience level"><Select value={v.programs.experience_level} onChange={setIn("programs", "experience_level")} options={["Beginner", "Mixed", "Intermediate", "Advanced"]} testId="field-exp-level" /></Field>
                </div>

                <SectionHead n="04" title="Partnership Interests" />
                <Field label="What interests your institution?"><PillGroup multi value={v.partnership.interests} onChange={setIn("partnership", "interests")} options={INTERESTS} testId="field-interests" /></Field>
                <Field label="Collaboration formats"><PillGroup multi value={v.partnership.formats} onChange={setIn("partnership", "formats")} options={FORMATS} testId="field-formats" /></Field>

                <SectionHead n="05" title="Credit & Hours" blurb="Credit is always determined by your institution — tell us your requirements." />
                <Field label="School credit / hour requirements & documentation"><TextArea rows={3} value={v.credit.requirements} onChange={setIn("credit", "requirements")} testId="field-credit-reqs" /></Field>
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Admin contact for hours / evaluations"><TextInput value={v.credit.admin_contact} onChange={setIn("credit", "admin_contact")} testId="field-admin-contact" /></Field>
                  <Field label="Participation"><Select value={v.credit.participation} onChange={setIn("credit", "participation")} options={["Individual students", "Class participation", "Both"]} testId="field-participation" /></Field>
                </div>

                <SectionHead n="06" title="Logistics" />
                <div className="grid gap-5 sm:grid-cols-2">
                  <Field label="Semester / dates"><TextInput value={v.logistics.semester_dates} onChange={setIn("logistics", "semester_dates")} testId="field-semester" /></Field>
                  <Field label="Min hours"><TextInput value={v.logistics.min_hours} onChange={setIn("logistics", "min_hours")} testId="field-min-hours" /></Field>
                  <Field label="Max hours"><TextInput value={v.logistics.max_hours} onChange={setIn("logistics", "max_hours")} testId="field-max-hours" /></Field>
                </div>
                <Field label="Institutional requirements"><PillGroup multi value={v.logistics.requirements} onChange={setIn("logistics", "requirements")} options={REQUIREMENTS} testId="field-inst-reqs" /></Field>
                <Field label="Anything else we should know?"><TextArea rows={3} value={v.notes} onChange={(x) => set("notes", x)} testId="field-notes" /></Field>

                <SectionHead n="07" title="Acknowledgment" />
                <AckCheck checked={v.acknowledgment} onChange={(c) => set("acknowledgment", c)} testId="ack-school-credit">
                  I acknowledge that academic, lab, practicum, or internship credit is determined by the educational institution.
                </AckCheck>

                <SubmitBar loading={loading} label="Submit Partnership Inquiry" loadingLabel="SENDING YOUR PARTNERSHIP INQUIRY..." />
              </form>
            </FormShell>
          )}
        </div>
      </section>
    </>
  );
}
